import { Alert, CircularProgress, Stack, useTheme } from "@mui/material";
import { type Palette } from "@mui/material/styles";
import Plot from "components/plotly/LocalisedPlot";
import { useTenantTranslation } from "hooks/formatters";
import { PlotData } from "plotly.js";
import { useCallback, useEffect, useMemo, useState } from "react";
import { sorted } from "src/helpers";
import {
  LabellingCostDailyByLocationEntry,
  useGetLabellingCostDailyByLocationQuery,
} from "src/store/api/generatedApi";

const buildLocationColors = (palette: Palette["data"]): string[] => [
  palette.blue,
  palette.pink,
  palette.lightblue,
  palette.orange,
  palette.green,
  palette.red,
];

type TenantDescriptor = Readonly<{
  tenantName: string;
  tenantLongName: string;
}>;

type CombinedCostRatioChartProps = Readonly<{
  tenants: TenantDescriptor[];
  start: string;
  end: string;
  groupBy: "session_date" | "footage_date";
  metricKey: "labelling_cost_ratio" | "review_cost_ratio";
}>;

type TenantLoadState = Readonly<{
  entries: LabellingCostDailyByLocationEntry[];
  isLoading: boolean;
}>;

type OnTenantLoaded = (
  tenantName: string,
  entries: LabellingCostDailyByLocationEntry[],
  isLoading: boolean
) => void;

const TenantLocationDataLoader = ({
  tenantName,
  start,
  end,
  groupBy,
  onLoaded,
}: Readonly<{
  tenantName: string;
  start: string;
  end: string;
  groupBy: "session_date" | "footage_date";
  onLoaded: OnTenantLoaded;
}>) => {
  const { data, isLoading } = useGetLabellingCostDailyByLocationQuery(
    { tenantName, start, end, groupBy },
    { skip: !start || !end }
  );
  const entries = data?.entries;

  useEffect(() => {
    onLoaded(tenantName, entries ?? [], isLoading);
  }, [tenantName, entries, isLoading, onLoaded]);

  return null;
};

const buildLocationTraces = (
  tenants: TenantDescriptor[],
  stateByTenant: Record<string, TenantLoadState>,
  metricKey: "labelling_cost_ratio" | "review_cost_ratio",
  colors: string[]
): Partial<PlotData>[] => {
  const locationTraces = tenants.flatMap((tenant) => {
    const entries = stateByTenant[tenant.tenantName]?.entries ?? [];
    const locationNames = sorted(
      [...new Set(entries.map((entry) => entry.location_name))],
      (name) => name
    );
    return locationNames.map((locationName) => ({
      label: `${tenant.tenantLongName} – ${locationName}`,
      entries: entries.filter((entry) => entry.location_name === locationName),
    }));
  });

  return locationTraces.map((trace, index) => {
    const byDate = sorted([...trace.entries], (entry) => entry.date);
    const color = colors[index % colors.length];
    return {
      x: byDate.map((entry) => entry.date),
      y: byDate.map((entry) => entry[metricKey]),
      type: "scatter" as const,
      mode: "lines+markers" as const,
      name: trace.label,
      marker: { color, size: 5 },
      line: { color, width: 2 },
    };
  });
};

export const CombinedCostRatioChart = ({
  tenants,
  start,
  end,
  groupBy,
  metricKey,
}: CombinedCostRatioChartProps) => {
  const { t } = useTenantTranslation();
  const { data: palette } = useTheme().palette;
  const locationColors = useMemo(() => buildLocationColors(palette), [palette]);
  const [stateByTenant, setStateByTenant] = useState<
    Record<string, TenantLoadState>
  >({});

  const handleTenantLoaded: OnTenantLoaded = useCallback(
    (tenantName, entries, isLoading) => {
      setStateByTenant((previous) => {
        const existing = previous[tenantName];
        if (existing?.entries === entries && existing.isLoading === isLoading) {
          return previous;
        }
        return { ...previous, [tenantName]: { entries, isLoading } };
      });
    },
    []
  );

  const allResolved = tenants.every((tenant) => {
    const state = stateByTenant[tenant.tenantName];
    return state !== undefined && !state.isLoading;
  });

  const allEmpty =
    allResolved &&
    tenants.every(
      (tenant) => (stateByTenant[tenant.tenantName]?.entries ?? []).length === 0
    );

  const traces = useMemo(
    () =>
      buildLocationTraces(tenants, stateByTenant, metricKey, locationColors),
    [tenants, stateByTenant, metricKey, locationColors]
  );

  return (
    <>
      {tenants.map((tenant) => (
        <TenantLocationDataLoader
          key={tenant.tenantName}
          tenantName={tenant.tenantName}
          start={start}
          end={end}
          groupBy={groupBy}
          onLoaded={handleTenantLoaded}
        />
      ))}
      {!allResolved ? (
        <Stack alignItems="center" py={4}>
          <CircularProgress size={24} />
        </Stack>
      ) : allEmpty ? (
        <Alert severity="info">{t("noDataAvailable")}</Alert>
      ) : (
        <Plot
          data={traces}
          layout={{
            autosize: true,
            xaxis: { type: "date" },
            yaxis: { title: { text: `${t("labelHoursPerHour")} (h/h)` } },
            margin: { l: 60, r: 30, t: 30, b: 60 },
            hovermode: "x unified",
            plot_bgcolor: "transparent",
            paper_bgcolor: "transparent",
            legend: { orientation: "h", y: -0.2 },
          }}
          config={{
            responsive: true,
            displayModeBar: false,
            displaylogo: false,
          }}
          style={{ width: "100%", height: "350px" }}
          useResizeHandler
        />
      )}
    </>
  );
};
